import { Typography, Grid, Box } from "@mui/material";
import { BarChart } from "@mui/x-charts";
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { cookies, instance } from "../../../../Controller/Common";
import { useSelector } from "react-redux";
import { RootState } from "../../../../Redux/UserSlice";

interface SeriesData {
  data: number[];
  stack: string;
  label: string;
  color: string;
  valueFormatter: any;
}

interface TimelineData {
  month: string;
  topicMinutes: number;
  assessmentMinutes: number;
}

const BarCharts: React.FC = () => {
  const userId =
    useSelector((state: RootState) => state.user.userID) ??
    cookies.get("userId");
  const [studyTimeLine, setStudyTimeLine] = useState<TimelineData[]>([]);

  const GetActivityCount = async () => {
    try {
      const response = await instance.get(
        `6D/activity/myDashboardCount?userId=${userId}`
      );
      if (response.status === 200) {
        setStudyTimeLine(response.data.StudyTimeLine);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    GetActivityCount();
  }, []);

  const parseDate = (dateStr: string): Date | null => {
    try {
      let date = new Date(dateStr);

      if (!isNaN(date.getTime())) {
        return date;
      }

      if (dateStr.includes(" ")) {
        const [month, year] = dateStr.split(" ");
        date = new Date(`${month} 1, ${year}`);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }

      return null;
    } catch {
      return null;
    }
  };

  const getFormattedMonth = (monthStr: string): string => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const date = parseDate(monthStr);

    if (!date) {
      console.error("Invalid date format:", monthStr);
      return "Invalid Date";
    }

    const month = months[date.getMonth()];
    const year = date.getFullYear().toString().slice(-2);

    return `${month} '${year}`;
  };

  const getGraphData = () => {
    if (!studyTimeLine.length) return [];

    const validData = studyTimeLine.filter(
      (item) => parseDate(item.month) !== null
    );

    const sortedData = [...validData].sort((a, b) => {
      const dateA = parseDate(a.month);
      const dateB = parseDate(b.month);

      if (!dateA || !dateB) return 0;
      return dateA.getTime() - dateB.getTime();
    });

    if (sortedData.length >= 6) {
      return sortedData.slice(-6);
    }

    const result: TimelineData[] = [...sortedData];

    if (result.length > 0) {
      const lastValidDate = parseDate(result[result.length - 1].month);

      if (lastValidDate) {
        let currentDate = new Date(lastValidDate);

        while (result.length < 6) {
          currentDate.setMonth(currentDate.getMonth() + 1);
          result.push({
            month: currentDate.toISOString().slice(0, 7),
            topicMinutes: 0,
            assessmentMinutes: 0,
          });
        }
      }
    }

    return result;
  };

  const graphData = getGraphData();

  const series: SeriesData[] = [
    {
      data: graphData.map((item) => item.topicMinutes),
      stack: "A",
      label: "Topics",
      color: "#5D87BD",
      valueFormatter(value: number | null) {
        return `${value} min`;
      },
    },
    {
      data: graphData.map((item) => item.assessmentMinutes),
      stack: "A",
      label: "Assessments",
      color: "#E5EDF3",
      valueFormatter(value: number | null) {
        return `${value} min`;
      },
    },
  ];

  return (
    <>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: "600",
          margin: "8px",
          color: "#3F3F40",
        }}
      >
        Study Time
      </Typography>
      <Grid
        container
        spacing={2}
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          flexDirection: "row",
        }}
      >
        <Grid item xs={12} sm={8}>
          <ChartBox>
            <BarChart
              xAxis={[
                {
                  scaleType: "band",
                  data: graphData.map((item) => getFormattedMonth(item.month)),
                  disableLine: true,
                  disableTicks: true,
                },
              ]}
              yAxis={[
                {
                  disableLine: true,
                  tickSize: 0,
                },
              ]}
              grid={{ horizontal: true }}
              sx={{
                ".MuiChartsGrid-line": {
                  stroke: "rgba(0, 0, 0, 0.2)",
                  strokeDasharray: "5 5",
                  strokeWidth: 1,
                },
                "& .MuiChartsAxis-tickLabel": {
                  fontFamily: "Poppins",
                  fontSize: "12px",
                  color: "#3F3F40",
                },
              }}
              series={series}
              borderRadius={5}
              height={250}
              legend={{ hidden: true }}
              slotProps={{
                noDataOverlay: {
                  message:
                    " No data available yet. Start exploring and learning to see updates here!",

                  sx: {
                    fontSize: "10px",
                    fontWeight: 400,
                    color: "#3F3F40",
                    fontFamily: "Poppins",
                  },
                },
              }}
            />
          </ChartBox>
        </Grid>
        <Grid item xs={12} sm={4}>
          <GridBox>
            <ChartBox1>
              <LabelBox>
                <BlueBox />
                &nbsp;&nbsp;
                <p
                  style={{
                    fontFamily: "poppins",
                    fontWeight: "400",
                    fontSize: "10px",
                    color: "#3F3F40",
                  }}
                >
                  Topics
                </p>
              </LabelBox>
              <LabelBox>
                <RedBox />
                &nbsp;&nbsp;
                <p
                  style={{
                    fontFamily: "poppins",
                    fontWeight: "400",
                    fontSize: "10px",
                    color: "#3F3F40",
                  }}
                >
                  Assessments
                </p>
              </LabelBox>
            </ChartBox1>
          </GridBox>
        </Grid>
      </Grid>
    </>
  );
};

export default BarCharts;

const ChartBox = styled(Box)`
  width: 100%;
  box-shadow: none !important;
  background-color: white;
  border-radius: 5px;
`;
const ChartBox1 = styled(Box)`
  box-shadow: none !important;
  background-color: white;
  border-radius: 5px;
  padding: 20px;
`;
const GridBox = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const LabelBox = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const BlueBox = styled(Box)`
  height: 20px;
  width: 20px;
  border-radius: 3px;
  background-color: #5d87bd;
`;

const RedBox = styled(Box)`
  height: 20px;
  width: 20px;
  border-radius: 3px;
  background-color: #e5edf3;
`;
